import { flow, pipe, Option, ReadonlyArray, Runtime } from "fp";

import * as Moment from "moment";
import React from "react";

import { Order } from "~/Order";
import { Search } from "~/Search";
import { Time } from "~/Time";

import { Component, Props } from "./Component";
import { Input as Input_ } from "./Input";

/**
 * An `Interval` can either be `Started`, i.e. ongoing, or `Stopped`.
 * ```
 * Started.start |-------------- ???
 * Stopped.start |-------------| Stopped.stop
 * ```
 */
export type Interval = Interval.Started | Interval.Stopped;

export function Interval(props: Props) {
  return <Component {...props} />;
}

export namespace Interval {
  export import Input = Input_;

  export function isStopped(interval: Interval): interval is Stopped {
    return "stop" in interval.interval && !!interval.interval.stop;
  }

  export const toHumanized = (interval: Interval): string => {
    const start = Time.DateTime.toHMMSS(
      Time.Interval.when(interval, {
        started: (started) => started.interval.start,
        stopped: (stopped) => stopped.interval.start,
      })
    );

    const ongoing = Time.Interval.when(interval, {
      started: () => "ongoing",
      stopped: (stopped) => Time.DateTime.toLocal(stopped.interval.stop),
    });

    return `${start} to ${ongoing}`;
  };

  /**
   * If `a.start` is `Order.greaterThan` `b.start`
   * ```
   *  a.start |------------- ???
   * b.start |------------- ???
   * ```
   * or `a.start` is `Order.lessThan` `b.start`
   * ```
   * a.start |------------- ???
   *  b.start |------------- ???
   * ```
   * then use their `Order.Ordering`.
   *
   * If `a` and `b` are both `Stopped`
   * ```
   * a.start |-------------| a.stop
   * b.start |--------------| b.stop
   * ```
   * `compare` their `stop`.
   *
   * If only `a` is `Stopped`
   * ```
   * a.start |-------------| a.stop
   * b.start |-------------- ???
   * ```
   * `a` is `Order.lessThan`.
   *
   * If only `b` is `Stopped`
   * ```
   * a.start |-------------- ???
   * b.start |-------------| b.stop
   * ```
   * `a` is `Order.greaterThan`.
   *
   * If both are only `Started`
   * ```
   * a.start |-------------- ???
   * b.start |-------------- ???
   * ```
   * `a` and `b` are `Order.equal`.
   */
  export const { equals, compare } = Order.fromCompare<Interval>((a, b) =>
    Time.DateTime.compare(a.interval.start, b.interval.start) !== Order.equal()
      ? Time.DateTime.compare(a.interval.start, b.interval.start)
      : isStopped(a) && isStopped(b)
      ? Time.DateTime.compare(a.interval.stop, b.interval.stop)
      : isStopped(a)
      ? Order.lessThan()
      : isStopped(b)
      ? Order.greaterThan()
      : Order.equal()
  );

  export const toSearch: Search.From<Interval> = (interval) =>
    when(interval, {
      started: ({ interval: { start } }) => Time.DateTime.toSearch(start),
      stopped: ({ interval: { start, stop } }) =>
        Search.join(
          Time.DateTime.toSearch(start),
          Time.DateTime.toSearch(stop)
        ),
    });

  export type When<A> = {
    readonly started: (started: Started) => A;
    readonly stopped: (stopped: Stopped) => A;
  };

  export function when<A>(interval: Interval, when: When<A>): A;
  export function when<A>(when: When<A>): (interval: Interval) => A;
  export function when<A>(
    whenOrInterval: Interval | When<A>,
    whenOrUndefined?: When<A>
  ) {
    if (whenOrUndefined)
      return when(whenOrUndefined)(whenOrInterval as Interval);

    const { started, stopped } = whenOrInterval as When<A>;
    return (interval: Interval): A =>
      isStopped(interval) ? stopped(interval) : started(interval);
  }

  export const fromDateTimes = (start: Time.DateTime) => (
    stop?: Time.DateTime | null
  ): Interval => ({
    interval: { start, stop: stop ?? undefined },
  });

  export const expand = ({
    duration,
  }: Time.Duration): ((interval: Interval) => Interval) =>
    when({
      started: ({ interval: { start } }) => ({
        interval: { start: { dateTime: start.dateTime - duration } },
      }),
      stopped: ({ interval: { start, stop } }) => ({
        interval: {
          start: { dateTime: start.dateTime - duration },
          stop: { dateTime: stop.dateTime + duration },
        },
      }),
    });

  export namespace Response {
    export const runtime = () =>
      Runtime.union([Started.Response.runtime(), Stopped.Response.runtime()]);

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Interval =>
      "end_date" in response || "end_time" in response
        ? Stopped.Response.decode(response)
        : Started.Response.decode(response);
  }

  export type Started = {
    readonly interval: { readonly start: Time.DateTime };
  };

  export namespace Started {
    export const toStopped = (stop: Time.DateTime) => ({
      interval: { start },
    }: Started): Stopped => ({
      interval: { start, stop },
    });

    export namespace Response {
      export const runtime = () =>
        Runtime.union([
          Runtime.strict({ start_date: Time.DateTime.Response.runtime() }),
          Runtime.strict({ start_time: Time.DateTime.Response.runtime() }),
        ]);

      export const decode = (
        response: Runtime.ToType<typeof runtime>
      ): Interval => ({
        interval: {
          start:
            "start_date" in response
              ? response.start_date
              : response.start_time,
        },
      });
    }
  }

  export type Stopped = Started & {
    readonly interval: { readonly stop: Time.DateTime };
  };

  export namespace Stopped {
    export const keyByDates = ({ interval }: Stopped): string =>
      pipe(
        [
          Time.DateTime.toStartOfDay(interval.start),
          Time.DateTime.toEndOfDay(interval.stop),
        ],
        ReadonlyArray.map(Time.DateTime.toDateOnly),
        (dates) => dates.join(",")
      );

    export const fromInterval = (orElseStop: () => Time.DateTime) => (
      interval: Interval
    ): Stopped =>
      when(interval, {
        started: Started.toStopped(orElseStop()),
        stopped: (stopped) => stopped,
      });

    export const fromDateTimes = (
      start: Time.DateTime,
      stop: Time.DateTime
    ): Stopped => ({
      interval: {
        start,
        stop,
      },
    });

    export const fromDateTimesStartEndDay = (
      start: Time.DateTime,
      stop: Time.DateTime
    ): Stopped => ({
      interval: {
        start: Time.DateTime.toStartOfDay({ dateTime: start.dateTime }),
        stop: Time.DateTime.toEndOfDay({ dateTime: stop.dateTime }),
      },
    });

    export const fromDateStringsStartEndDay = (start: string) => (
      stop: string
    ): Option<Stopped> =>
      pipe(
        [start, stop],
        ReadonlyArray.filterMap(
          flow(Time.DateTime.fromString, Option.fromEither)
        ),
        Option.fromPredicate(
          (dates): dates is readonly [Time.DateTime, Time.DateTime] =>
            dates[0] !== undefined && dates[1] !== undefined
        ),
        Option.map(([start, stop]) => fromDateTimesStartEndDay(start, stop))
      );

    export const fromDateStrings = (start: string) => (
      stop: string
    ): Option<Stopped> =>
      pipe(
        [start, stop],
        ReadonlyArray.filterMap(
          flow(Time.DateTime.fromString, Option.fromEither)
        ),
        Option.fromPredicate(
          (dates): dates is readonly [Time.DateTime, Time.DateTime] =>
            dates[0] !== undefined && dates[1] !== undefined
        ),
        Option.map(([start, stop]) => fromDateTimes(start, stop))
      );

    /**
     * Example collisions...
     * ```
     *  a.start |-------------| a.stop
     *  b.start |-------------| b.stop
     *
     *  a.start |-------------| a.stop
     *   b.start |-------------| b.stop
     *
     *   a.start |-------------| a.stop
     *  b.start |-------------| b.stop
     *
     *  a.start |-------| a.stop
     *          b.start |-------| b.stop
     *
     *          a.start |-------| a.stop
     *  b.start |-------| b.stop
     *
     *  a.start |-------------| a.stop
     *   b.start |-----------| b.stop
     *
     *   a.start |-----------| a.stop
     *  b.start |-------------| b.stop
     * ```
     */
    export const collides = (a: Stopped) => (b: Stopped): boolean =>
      contains(a.interval.start)(b) ||
      contains(a.interval.stop)(b) ||
      contains(b.interval.start)(a) ||
      contains(b.interval.stop)(a);

    /** Does the `dateTime` fall between the interval's `start` and `stop`? */
    export const contains = ({ dateTime }: Time.DateTime) => ({
      interval: { start, stop },
    }: Stopped): boolean =>
      start.dateTime <= dateTime && dateTime <= stop.dateTime;

    /**
     * Is `b` completely contained within `a`
     *  ```
     *  a.start |-------------| a.stop
     *   b.start |-----------| b.stop
     * ```
     **/
    export const contained = (a: Time.Interval.Stopped) => (
      b: Time.Interval.Stopped
    ) =>
      Time.Interval.Stopped.contains(b.interval.start)(a) &&
      Time.Interval.Stopped.contains(b.interval.stop)(a);

    /** What is the `Duration` between an interval's `start` and `stop`? */
    export const toDuration = ({
      interval: { start, stop },
    }: Stopped): Time.Duration => ({
      duration: Moment.default(stop.dateTime).diff(start.dateTime).valueOf(),
    });

    export const expand = ({ duration }: Time.Duration) => ({
      interval: { start, stop },
    }: Stopped): Stopped => ({
      interval: {
        start: { dateTime: start.dateTime - duration },
        stop: { dateTime: stop.dateTime + duration },
      },
    });

    export const expandRight = ({
      duration,
    }: Time.Duration): ((interval: Stopped) => Stopped) => ({
      interval: { start, stop },
    }: Stopped) => ({
      interval: {
        start: { dateTime: start.dateTime },
        stop: { dateTime: stop.dateTime + duration },
      },
    });

    export const expandLeft = ({
      duration,
    }: Time.Duration): ((interval: Stopped) => Stopped) => ({
      interval: { start, stop },
    }: Stopped) => ({
      interval: {
        start: { dateTime: start.dateTime - duration },
        stop: { dateTime: stop.dateTime },
      },
    });

    export const toRangeOfDates = ({
      interval,
    }: Stopped): readonly Time.DateTime[] =>
      pipe(
        Time.Duration.toDays(toDuration({ interval })) + 1,
        (days) => Array.from(Array(days).keys()),
        ReadonlyArray.map((day) =>
          Time.DateTime.fromNumber(
            interval.start.dateTime + Time.Duration.days(day).duration
          )
        )
      );

    export const spy = <A extends Time.Interval.Stopped>(
      value: A,
      message = `interval:`
    ) => {
      console.log(
        `${message} start=${Time.DateTime.toLocal(
          value.interval.start
        )} stop=${Time.DateTime.toLocal(value.interval.stop)}`
      );
      return value;
    };

    export namespace Response {
      export const runtime = () =>
        Runtime.intersection([
          Started.Response.runtime(),
          Runtime.union([
            Runtime.strict({ end_date: Time.DateTime.Response.runtime() }),
            Runtime.strict({ end_time: Time.DateTime.Response.runtime() }),
          ]),
        ]);

      export const decode = (
        response: Runtime.ToType<typeof runtime>
      ): Stopped =>
        pipe(
          Started.Response.decode(response),
          Time.Interval.Started.toStopped(
            "end_date" in response ? response.end_date : response.end_time
          )
        );
    }
  }
}
